import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useLocation } from 'react-router-dom'
import { Collection } from 'lib/components/collection'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { JourneyBar } from 'lib/components/journeyBar'
import { JewelryType } from 'constants/jewelryTypes'
import SimpleStore from 'stores/simpleStore'
import StoreFunctions from 'stores/storeFunctions'
import logger from 'utils/logger'
import { useAppContext } from 'lib/context/AppContext'

interface CollectionPageState {
  productIds: string[]
  diamondsInfo: {
    type: string
    shape: string
    weight: string
    sku: string
    clarity: string
    color: string
  }[]
  shape: string
  errorModalOpen: boolean
  error: { [key: string]: any }
}

export const CollectionPage: React.FC<any> = props => {
  const { t } = useTranslation()
  const { currencyInfo } = useAppContext()
  const { id } = useParams<{ id: string }>()
  const [productIds, setProductIds] = useState<CollectionPageState['productIds']>([])
  const [diamondsInfo, setDiamondsInfo] = useState<CollectionPageState['diamondsInfo']>([])
  const [shape, setShape] = useState<CollectionPageState['shape'] | null>(null)
  const [mode, setMode] = useState<string>(JewelryType.Ring)
  const [errorModalOpen, setErrorModalOpen] = useState<CollectionPageState['errorModalOpen']>(false)
  const [error, setError] = useState<CollectionPageState['error']>({})

  const simpleStore = new SimpleStore()
  const storeFunctions = new StoreFunctions()

  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  const fetchProducts = async (): Promise<void> => {
    let error: null | string = null
    let errorBody: null | string = null
    try {
      const diamondsData = await simpleStore.ajax('GET', `ds/get_diamond/${id}`)
      const dData = diamondsData.gems
      if (dData) {
        setShape(dData[0].shape)
        setDiamondsInfo(dData)
        let mode = dData.length > 1 ? 'pair' : 'single'
        const searchParams = new URLSearchParams(window.location.search)
        const jType = searchParams.get('jewelry_type')
        if (jType == 'ring') {
          mode = JewelryType.Ring
        } else if (jType == 'earring') {
          mode = JewelryType.Earring
        } else if (jType == 'pendant') {
          mode = JewelryType.Pendant
        } else if (jType == 'bracelet') {
          mode = JewelryType.Bracelet
        }
        
        setMode(mode)
        // Only resolve the matching product IDs here (cheap). The Collection
        // component fetches the product_data for all matched products.
        const matchedIds = await storeFunctions.tagProductsForSKU(dData, mode)

        if (Array.isArray(matchedIds) && matchedIds.length) {
          setProductIds(matchedIds)
          return
        }
        error = t('collection.noProductsFound')
        errorBody = t('collection.cannotRetrieveProducts')
      } else {
        error = t('collection.diamondNotAvailable')
        errorBody = t('collection.diamondOutOfStock')
      }
    } catch (e) {
      logger.info(e)
      error = t('collection.applicationError')
      errorBody = ''
    }
    setError({
      title: error,
      message: errorBody
    })
    setErrorModalOpen(true)
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const closeErrorModal = (): void => {
    setError({})
    setErrorModalOpen(false)
    // The collection view can't recover from a diamond/product lookup failure
    // (it would sit on empty skeletons), so send the user back to diamond search.
    window.location.href = window.location.origin + '/apps/ub/diamonds'
  }

  const handleProductsLoadError = (): void => {
    setError({
      title: t('collection.noProductsFound'),
      message: t('collection.cannotRetrieveProducts')
    })
    setErrorModalOpen(true)
  }

  return (
    <>
      <ErrorModal
        open={errorModalOpen}
        title={error.title}
        message={error.message}
        onClose={closeErrorModal}
      />
      <JourneyBar activeStep={2} selectedDiamonds={diamondsInfo} mode={mode} />
      <Collection
        currencyInfo={currencyInfo}
        shape={shape}
        productIds={productIds}
        selectedDiamondSKU={id}
        onError={handleProductsLoadError}
      />
    </>
  )
}

export default CollectionPage
